import React, { useCallback, useEffect, useState } from 'react';
import {
  CommentScope,
  CommentContext,
  getComment,
  saveComment,
} from '../api/comments';
import './EntityComment.css';

interface Props {
  scope: CommentScope;
  context: CommentContext;
  title?: string;
  // Bump this from the parent (e.g. after a voice note transcribes) to re-fetch
  // the auto-filled comment.
  refreshKey?: number;
}

const EntityComment: React.FC<Props> = ({ scope, context, title, refreshKey }) => {
  const [value, setValue] = useState('');
  const [saved, setSaved] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [justSaved, setJustSaved] = useState(false);

  // Re-fetch whenever the entity context or refreshKey changes.
  const contextKey = JSON.stringify({ scope, context });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getComment(scope, context);
      // Don't clobber an in-progress edit: only adopt the server value when the
      // local text still matches what we last loaded/saved.
      setValue((prev) => (prev === saved ? data.comment : prev));
      setSaved(data.comment);
    } catch (err: any) {
      setError(err?.message || 'Failed to load comment');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextKey, refreshKey]);

  useEffect(() => {
    load();
  }, [load]);

  const onSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const data = await saveComment(scope, context, value);
      setSaved(data.comment);
      setValue(data.comment);
      setJustSaved(true);
      window.setTimeout(() => setJustSaved(false), 1500);
    } catch (err: any) {
      setError(err?.message || 'Failed to save comment');
    } finally {
      setSaving(false);
    }
  };

  const dirty = value !== saved;

  return (
    <div className={`entity-comment entity-comment--${scope}`}>
      <div className="ec-head">
        <span className="ec-title">{title || 'Comment'}</span>
        {loading && <span className="ec-hint">Loading…</span>}
        {!loading && justSaved && <span className="ec-hint ec-hint--ok">Saved</span>}
        {!loading && dirty && !justSaved && <span className="ec-hint">Unsaved changes</span>}
      </div>
      <textarea
        className="ec-area"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Type a comment for this race or horse."
        rows={3}
      />
      {error && <div className="ec-error">{error}</div>}
      <div className="ec-actions">
        <button
          type="button"
          className="ec-btn ec-btn--save"
          onClick={onSave}
          disabled={saving || !dirty}
        >
          {saving ? 'Saving…' : 'Save comment'}
        </button>
        {dirty && (
          <button
            type="button"
            className="ec-btn"
            onClick={() => setValue(saved)}
            disabled={saving}
          >
            Revert
          </button>
        )}
      </div>
    </div>
  );
};

export default EntityComment;
